// screens/SignIndexScreen.tsx — FP6 Appendice v2 "Segnali" section.
// Routes:
//   /appendice/segnali          → searchable sign index (name + figura + category)
//   /appendice/segnali/:signId  → full sign sheet (shape/glyph/meaning/traps)
//
// "Segnali" is a SUB-section of Appendice (not a new top-level drawer item), so
// the 5 current student-facing top-level sections stay intact per the brand rules.
// The index ships in the bundle (sign-index.json, 386 rows); the long per-sign
// sheets load lazily (sign-details.json) only on the detail view.

import type { JSX } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useTokens } from '../state/theme';
import {
  SIGN_INDEX, getSignById, signCategories, SIGN_CATEGORY_LABELS,
  loadSignDetails, type SignDetail, type SignIndexEntry,
} from '../data/signs';
import { searchSigns } from '../utils/appendice-search';

function imageSrc(image: string | null): string | null {
  if (!image) return null;
  return `/${image.includes('/') ? image : `img_sign/${image}`}`;
}

// ---------------------------------------------------------------------------
// Index (list + search + category filter)
// ---------------------------------------------------------------------------

export default function SignIndexScreen(): JSX.Element {
  const navigate = useNavigate();
  const { signId } = useParams<{ signId?: string }>();

  if (signId) {
    return <SignDetailView signId={signId} />;
  }

  return <SignIndexView navigate={navigate} />;
}

function SignIndexView({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}): JSX.Element {
  const tokens = useTokens();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const cats = useMemo(() => signCategories(), []);

  const results = useMemo<SignIndexEntry[]>(() => {
    let pool = SIGN_INDEX;
    if (category) pool = pool.filter((s) => s.category === category);
    const term = search.trim();
    if (term.length < 2) return pool;
    const ranked = searchSigns(term, 400);
    const ids = new Set(ranked.map((r) => r.id));
    // Preserve ranked order but constrain to the category filter.
    return ranked.map((r) => getSignById(r.id)!).filter((s) => s && (!category || s.category === category) && ids.has(s.id));
  }, [search, category]);

  return (
    <Screen>
      {/* V29 S4: the chrome back arrow (S1) is the single back affordance —
          the in-body "‹ Appendice/Segnali" buttons were the app's only
          in-page backs (audit nav-inconsistency). */}
      {/* Hierarchical back (2026-07-14 r4): one level up, deterministic. */}
      <TopChrome title="Segnali" subtitle="Appendice" onBack={() => navigate('/appendice', { replace: true })} />

      {/* Search */}
      <div style={{ padding: '8px 16px 0' }}>
        <div style={searchBox(tokens)}>
          <SearchIcon color={tokens.textFaint} />
          <input
            type="search"
            placeholder="Cerca segnale, figura II.xx…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInput(tokens)}
          />
        </div>
      </div>

      {/* Category family chips */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '10px 16px 4px' }}>
        <Chip label="Tutti" active={category === null} onClick={() => setCategory(null)} tokens={tokens} />
        {cats.map((c) => (
          <Chip key={c} label={SIGN_CATEGORY_LABELS[c] ?? c} active={category === c} onClick={() => setCategory(category === c ? null : c)} tokens={tokens} />
        ))}
      </div>

      <main className="asc-scroll" style={{ flex: 1, overflow: 'auto', padding: '8px 16px 24px' }}>
        <div style={{ fontFamily: tokens.fonts.mono, fontSize: 11, color: tokens.textMuted, margin: '4px 2px 10px' }}>
          {results.length} {results.length === 1 ? 'segnale' : 'segnali'}
        </div>
        {results.length === 0 ? (
          <div style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, padding: '24px 0', textAlign: 'center' }}>
            Nessun segnale per “{search}”.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {results.map((s) => (
              <SignCard key={s.id} sign={s} onClick={() => navigate(`/appendice/segnali/${s.id}`)} tokens={tokens} />
            ))}
          </div>
        )}
      </main>
    </Screen>
  );
}

function SignCard({ sign, onClick, tokens }: { sign: SignIndexEntry; onClick: () => void; tokens: ReturnType<typeof useTokens> }): JSX.Element {
  const src = imageSrc(sign.image);
  return (
    <button
      onClick={onClick}
      data-testid={`sign-card-${sign.id}`}
      style={{
        background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg,
        padding: 10, cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 76, background: tokens.bg, borderRadius: tokens.radius.md }}>
        {src ? (
          <img
            src={src}
            alt={sign.name}
            style={{ maxWidth: '100%', maxHeight: 72, height: 'auto', display: 'block' }}
            decoding="async" loading="lazy"
            onError={(e) => { (e.currentTarget.style.display = 'none'); }}
          />
        ) : null}
      </div>
      <div>
        <div style={{ fontFamily: tokens.fonts.body, fontSize: 13, fontWeight: 600, color: tokens.text, lineHeight: 1.25, letterSpacing: -0.1 }}>
          {sign.name}
        </div>
        {sign.figura && (
          <div style={{ fontFamily: tokens.fonts.mono, fontSize: 10, color: tokens.textFaint, marginTop: 3 }}>
            {sign.figura}
          </div>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

function SignDetailView({
  signId,
}: {
  signId: string;
}): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const sign = getSignById(signId);
  const [details, setDetails] = useState<Record<string, SignDetail> | null>(null);
  const [detailsError, setDetailsError] = useState(false);

  useEffect(() => {
    let alive = true;
    loadSignDetails()
      .then((d) => { if (alive) setDetails(d); })
      .catch(() => { if (alive) setDetailsError(true); });
    return () => { alive = false; };
  }, []);

  const goChiedi = useCallback(() => {
    if (!sign) return;
    navigate('/prof', {
      state: {
        prefill: `Spiegami il segnale "${sign.name}"${sign.figura ? ` (${sign.figura})` : ''} e quando lo trovo all'esame.`,
        autoSend: true,
        ...(sign.image ? {
          figureReference: { kind: 'asset', imageRef: imageSrc(sign.image)!.slice(1) },
          figurePreview: { imageRef: imageSrc(sign.image)!.slice(1), label: sign.name },
        } : {}),
      },
    });
  }, [navigate, sign]);

  if (!sign) {
    return (
      <Screen>
        <TopChrome title="Segnale" subtitle="Appendice" onBack={() => navigate('/appendice/segnali', { replace: true })} />
        <main className="asc-scroll" style={{ flex: 1, overflow: 'auto', padding: '24px 16px' }}>
          <div style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.textMuted, textAlign: 'center' }}>
            Segnale non trovato.
          </div>
        </main>
      </Screen>
    );
  }

  const detail = details?.[signId] ?? null;
  const src = imageSrc(sign.image);
  const related = (detail?.related ?? []).map((id) => getSignById(id)).filter(Boolean) as SignIndexEntry[];

  return (
    <Screen>
      <TopChrome title={sign.name} subtitle="Segnale" onBack={() => navigate('/appendice/segnali', { replace: true })} />

      <main className="asc-scroll" style={{ flex: 1, overflow: 'auto', padding: '12px 16px 32px' }}>
        {/* Sign image */}
        {src && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 16px' }}>
            <div style={{ background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.lg, padding: 16, maxWidth: 220 }}>
              <img
                src={src}
                alt={sign.name}
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                decoding="async"
                onError={(e) => { const p = e.currentTarget.parentElement; if (p) p.style.display = 'none'; }}
              />
            </div>
          </div>
        )}

        {/* Meta line: figura · category · article */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {sign.figura && <Meta label={sign.figura} tokens={tokens} />}
          {sign.category && <Meta label={SIGN_CATEGORY_LABELS[sign.category] ?? sign.category} tokens={tokens} />}
          {sign.article && <Meta label={sign.article} tokens={tokens} />}
        </div>

        {/* Shape / glyph / meaning / traps from the lazy detail sheet */}
        {detailsError && (
          <div style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted, marginBottom: 12 }}>
            {sign.meaning || 'Dettaglio non disponibile in questo momento.'}
          </div>
        )}
        {!detailsError && !details && (
          <div data-testid="sign-detail-loading" style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted, marginBottom: 12 }}>
            Caricamento scheda…
          </div>
        )}

        {detail?.shape && <Field label="Forma" value={detail.shape} tokens={tokens} />}
        {detail?.glyph && <Field label="Simbolo" value={detail.glyph} tokens={tokens} />}
        {(detail?.meaning || sign.meaning) && <Field label="Significato" value={detail?.meaning ?? sign.meaning} tokens={tokens} />}
        {detail?.traps && <Field label="Trappole · da non confondere" value={detail.traps} accent tokens={tokens} />}

        {/* Related (confusion-pair) signs */}
        {related.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div style={fieldLabel(tokens)}>Segnali che si confondono</div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
              {related.map((r) => {
                const rsrc = imageSrc(r.image);
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/appendice/segnali/${r.id}`)}
                    data-testid={`related-${r.id}`}
                    style={{ flexShrink: 0, width: 96, background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: 8, cursor: 'pointer', textAlign: 'center' }}
                  >
                    {rsrc && <img src={rsrc} alt={r.name} style={{ maxWidth: '100%', maxHeight: 52, height: 'auto', display: 'block', margin: '0 auto 6px' }} decoding="async" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                    <span style={{ display: 'block', fontFamily: tokens.fonts.body, fontSize: 11, color: tokens.text, lineHeight: 1.2 }}>{r.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Chiedi al Prof CTA */}
        <div style={{ marginTop: 24 }}>
          <button
            onClick={goChiedi}
            style={{
              width: '100%', minHeight: 48, background: tokens.accent, border: 'none', borderRadius: tokens.radius.pill,
              padding: '12px 18px', color: tokens.mode === 'dark' ? '#1a1510' : '#fff',
              fontFamily: tokens.fonts.body, fontSize: 14.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Chiedi al Prof di questo segnale
          </button>
        </div>
      </main>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Field({ label, value, accent, tokens }: { label: string; value: string; accent?: boolean; tokens: ReturnType<typeof useTokens> }): JSX.Element {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={fieldLabel(tokens)}>{label}</div>
      <p
        style={{
          margin: 0, fontFamily: tokens.fonts.body, fontSize: 14.5, lineHeight: 1.55,
          color: accent ? tokens.text : tokens.textMuted,
          background: accent ? tokens.accentSoft : 'transparent',
          border: accent ? `1px solid ${tokens.accent}` : 'none',
          borderRadius: accent ? tokens.radius.md : 0,
          padding: accent ? '12px 14px' : 0,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function fieldLabel(tokens: ReturnType<typeof useTokens>) {
  return {
    fontFamily: tokens.fonts.mono, fontSize: 10, fontWeight: 600, color: tokens.textFaint,
    letterSpacing: 0.6, textTransform: 'uppercase' as const, margin: '0 2px 8px',
  };
}

function Meta({ label, tokens }: { label: string; tokens: ReturnType<typeof useTokens> }): JSX.Element {
  return (
    <span style={{ fontFamily: tokens.fonts.mono, fontSize: 11, color: tokens.textMuted, background: tokens.surface2, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '4px 10px' }}>
      {label}
    </span>
  );
}

function Chip({ label, active, onClick, tokens }: { label: string; active: boolean; onClick: () => void; tokens: ReturnType<typeof useTokens> }): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        flexShrink: 0, cursor: 'pointer', whiteSpace: 'nowrap',
        background: active ? tokens.accent : tokens.surface,
        color: active ? (tokens.mode === 'dark' ? '#1a1510' : '#fff') : tokens.textMuted,
        border: `1px solid ${active ? tokens.accent : tokens.line}`,
        borderRadius: tokens.radius.pill, padding: '6px 12px',
        fontFamily: tokens.fonts.body, fontSize: 12.5, fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}

function searchBox(tokens: ReturnType<typeof useTokens>) {
  return { display: 'flex' as const, alignItems: 'center' as const, gap: 8, background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '8px 12px' };
}
function searchInput(tokens: ReturnType<typeof useTokens>) {
  return { flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.text };
}

function SearchIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6" cy="6" r="4" />
      <path d="M9 9l3 3" />
    </svg>
  );
}
